import { Modes, SupportedModes } from './modes.enum';

// ResizeObserver
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

export class AspectRatio {
  /**
   * variables
   */
  host: HTMLElement;
  breakpointBase;
  // eslint-disable-next-line no-magic-numbers
  ratio: number = 4 / 3;

  ratioTolerance: number = 50;
  ratioTarget: string = 'img';
  viewport: HTMLElement;

  delay: number = 1000;
  limit: number;
  handleMode: SupportedModes;

  /**
   * constructor
   * @param host
   * @param ratio
   */
  constructor(host, ratio?: number) {
    this.setHost(host);
    this.setHandleMode(Modes.HEIGHT);

    if (ratio) {
      this.setRatio(ratio);
    }
  }

  setHandleMode(mode: SupportedModes) {
    this.handleMode = mode;
  }

  getHandleMode(): SupportedModes {
    return this.handleMode;
  }

  setLimit(value) {
    this.limit = value;
  }

  getLimit() {
    return this.limit;
  }

  setHost(host) {
    this.host = host;
  }

  getHost() {
    return this.host;
  }

  setRatio(ratio: number) {
    this.ratio = ratio;
  }

  getRatio() {
    return this.ratio;
  }

  setRatioTolerance(ratioTolerance: number) {
    this.ratioTolerance = ratioTolerance;
  }

  getRatioTolerance() {
    return this.ratioTolerance;
  }

  setAspectRatioTarget(targetTagName) {
    this.ratioTarget = targetTagName;
  }

  getAspectRatioTarget() {
    return this.ratioTarget;
  }

  setBreakpointBase(element) {
    this.breakpointBase = this.createBreakpointEntry(element);
  }

  getBreakpointBase() {
    return this.breakpointBase;
  }

  /**
   * createBreakpointEntry
   * @param element
   * @private
   */
  private createBreakpointEntry(element) {
    const aspectRatioTarget = this.getAspectRatioTarget();
    const aspectRatioTargetElement = element.querySelector(aspectRatioTarget);
    let originalWidth = aspectRatioTargetElement[aspectRatioTarget == 'img' ? 'naturalWidth' : 'clientWidth'];
    let originalHeight = aspectRatioTargetElement[aspectRatioTarget == 'img' ? 'naturalHeight' : 'clientHeight'];

    if (
      aspectRatioTarget == 'img' &&
      originalWidth < aspectRatioTargetElement.clientWidth &&
      originalHeight < aspectRatioTargetElement.clientHeight
    ) {
      originalWidth = aspectRatioTargetElement.clientWidth;
      originalHeight = aspectRatioTargetElement.clientHeight;
    }

    return {
      width: Math.floor(element ? element.getBoundingClientRect().width : 0),
      height: Math.floor(element ? element.getBoundingClientRect().height : 0),
      originalWidth: originalWidth,
      originalHeight: originalHeight,
      element: element ? element : null,
    };
  }

  /**
   * hasValidRatio
   * @param width
   * @param height
   * @private
   */
  hasValidRatio(width, height) {
    // eslint-disable-next-line no-magic-numbers
    return Math.abs(width / height) <= this.ratio + this.ratio * ((this.ratioTolerance / 100) * 5);
  }

  /**
   * Calculates the proper width of an element with a custom height, preserving the original aspect ratio.
   *
   * @param element
   * @param height
   */
  determineWidth(element, height) {
    return (element.originalWidth / element.originalHeight) * height;
  }

  /**
   * Calculates the proper height of an element with a custom width, preserving the original aspect ratio.
   *
   * @param element
   * @param width
   */
  determineHeight(element, width) {
    return (element.originalHeight / element.originalWidth) * width;
  }

  /**
   * handle
   * @param elements
   * @param viewport
   */
  handle(elements, viewport): Promise<HTMLElement> {
    this.viewport = viewport.getBoundingClientRect();

    return new Promise((resolve, reject) => {
      try {
        setTimeout(() => {
          this.breakpointBase = { width: 0, height: 0, image: null };

          elements.forEach((element) => {
            const bounding = element.getBoundingClientRect();

            // only elements are relevant that greater as the last known width
            if (
              this.getBreakpointBase() &&
              this.getBreakpointBase().element != element &&
              this.hasValidRatio(bounding.width, bounding.height) &&
              bounding.width > this.getBreakpointBase().width
            ) {
              // save element that is the relevant base based of its dimensions for all resizes to index zero
              this.setBreakpointBase(element);
            }
          });

          const resizeObserver = new ResizeObserver(() => {
            this.viewport = viewport.getBoundingClientRect();
            this.handleViewportResize();
          });

          resizeObserver.observe(viewport);

          resolve(this.viewport);
        }, this.delay);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * handleViewportResize
   */
  handleViewportResize() {
    const base = this.getBreakpointBase(),
      mode = this.getHandleMode();
    const viewport = this.viewport[mode == Modes.HEIGHT ? 'width' : 'height'];
    if (viewport < base[mode == Modes.HEIGHT ? 'originalWidth' : 'originalHeight']) {
      const determinedValue =
          mode == Modes.HEIGHT ? this.determineHeight(base, viewport) : this.determineWidth(base, viewport),
        limit = this.getLimit();
      if (limit && (determinedValue < limit || limit == 0)) {
        this.resize(determinedValue);
      }
    } else {
      this.reset();
    }
  }

  /**
   * resize
   * @param value
   * @private
   */
  private resize(value) {
    const host = this.getHost(),
      mode = this.getHandleMode();

    host.dispatchEvent(
      new CustomEvent('on:' + this.getEventNameHost() + ':aspect-ratio:' + mode + ':resize', {
        detail: {
          [mode]: value,
        },
      }),
    );
    host.style.setProperty('--aspect-ratio-viewport-' + mode + '-forced', value + 'px');
  }

  /**
   * reset
   * @private
   */
  private reset() {
    const host = this.getHost(),
      mode = this.getHandleMode();

    host.dispatchEvent(new CustomEvent('on:' + this.getEventNameHost() + ':aspect-ratio:' + mode + ':reset'));
    host.style.removeProperty('--aspect-ratio-viewport-' + mode + '-forced');
  }

  /**
   * getEventNameHost
   * @private
   */
  private getEventNameHost() {
    return this.getHost().tagName.split('-').slice(1).join('-').toLowerCase();
  }
}
